import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { ChevronDownIcon, ChevronUpIcon, EyeIcon } from '@heroicons/react/20/solid';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

function Parts() {

  document.title = 'Parts';

  const { user } = useAuth();
  const [inventoryData, setInventoryData] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterShop, setFilterShop] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [shops, setShops] = useState([]);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [createPartModalOpen, setCreatePartModalOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partHistory, setPartHistory] = useState({ productionLogs: [], deliveries: [] });
  const [newPart, setNewPart] = useState({
    part_number_master: '', // Changed from part_number to part_number_master
    revision: '',           // Added revision field
    description: '',
    inventory_quantity: '',
    minimum_stock_level: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setLoading(true);
      try {
        // Fetch inventory insights from the view
        const { data: inventory, error: invError } = await supabase
          .from('inventory_insights')
          .select('*');
        if (invError) throw invError;

        // Fetch production logs for history
        const { data: productionLogs, error: prodError } = await supabase
          .from('production_logs')
          .select('part_number, quantity_produced, production_date, shop_id, shops(name)')
          .order('production_date', { ascending: false });
        if (prodError) throw prodError;

        // Fetch deliveries for history
        const { data: deliveries, error: delError } = await supabase
          .from('deliveries')
          .select('part_number, quantity, ship_date')
          .order('ship_date', { ascending: false });
        if (delError) throw delError;

        // Fetch shops
        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw shopsError;
        setShops(shopsData || []);

        setInventoryData(inventory);
        setFilteredInventory(inventory);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(`Failed to fetch inventory data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sortedInventory = [...filteredInventory].sort((a, b) => {
      const aValue = a[field] || 0;
      const bValue = b[field] || 0;
      if (field === 'associated_shops') {
        const aShops = a[field]?.join(', ') || '';
        const bShops = b[field]?.join(', ') || '';
        return newDirection === 'asc' ? aShops.localeCompare(bShops) : bShops.localeCompare(aShops);
      }
      if (typeof aValue === 'number') return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      return newDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setFilteredInventory(sortedInventory);
  };

  const handleFilter = () => {
    let filtered = [...inventoryData];
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.part_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterShop) {
      filtered = filtered.filter(item => item.associated_shops.includes(filterShop));
    }
    if (filterStatus) {
      filtered = filtered.filter(item => item.stock_status === filterStatus);
    }
    setFilteredInventory(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [searchTerm, filterShop, filterStatus]);

  const viewPartHistory = async (part_number) => {
    try {
      const { data: productionLogs, error: prodError } = await supabase
        .from('production_logs')
        .select('quantity_produced, production_date, shop_id, shops(name)')
        .eq('part_number', part_number)
        .order('production_date', { ascending: false })
        .limit(10);
      if (prodError) throw prodError;

      const { data: deliveries, error: delError } = await supabase
        .from('deliveries')
        .select('quantity, ship_date')
        .eq('part_number', part_number)
        .order('ship_date', { ascending: false })
        .limit(10);
      if (delError) throw delError;

      setPartHistory({ productionLogs: productionLogs || [], deliveries: deliveries || [] });
      setSelectedPart(part_number);
      setHistoryModalOpen(true);
    } catch (err) {
      console.error('Error fetching part history:', err);
      toast.error(`Failed to fetch part history: ${err.message}`);
    }
  };

  const getStockStatusClass = (availableStock, minimumStockLevel) => {
    if (availableStock <= 0) return 'bg-red-100 text-red-800';
    if (availableStock < minimumStockLevel) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const validateForm = () => {
    const errors = {};
    if (!newPart.part_number_master.trim()) {
      errors.part_number_master = 'Part number master is required';
    }
    if (!newPart.description.trim()) {
      errors.description = 'Description is required';
    }
    if (newPart.inventory_quantity === '' || newPart.inventory_quantity < 0) {
      errors.inventory_quantity = 'Inventory quantity must be a non-negative number';
    }
    if (newPart.minimum_stock_level === '' || newPart.minimum_stock_level < 0) {
      errors.minimum_stock_level = 'Minimum stock level must be a non-negative number';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreatePart = async () => {
    if (!validateForm()) return;

    try {
      const { error } = await supabase
        .from('parts')
        .insert({
          part_number_master: newPart.part_number_master.trim(),
          revision: newPart.revision.trim() || null, // Allow revision to be empty (trigger will set part_number accordingly)
          description: newPart.description.trim(),
          inventory_quantity: parseInt(newPart.inventory_quantity, 10),
          minimum_stock_level: parseInt(newPart.minimum_stock_level, 10),
        });
      if (error) throw new Error(`Failed to create part: ${error.message}`);

      // Refresh inventory data
      const { data: updatedInventory, error: invError } = await supabase
        .from('inventory_insights')
        .select('*');
      if (invError) throw invError;

      setInventoryData(updatedInventory);
      setFilteredInventory(updatedInventory);

      setNewPart({
        part_number_master: '',
        revision: '',
        description: '',
        inventory_quantity: '',
        minimum_stock_level: '',
      });
      setFormErrors({});
      setCreatePartModalOpen(false);
      toast.success('Part created successfully!');
    } catch (err) {
      console.error('Error creating part:', err);
      toast.error(`Failed to create part: ${err.message}`);
    }
  };

  // Calculate key metrics for dashboard
  const totalParts = inventoryData.length;
  const lowStockCount = inventoryData.filter(item => item.stock_status === 'Low Stock').length;
  const outOfStockCount = inventoryData.filter(item => item.stock_status === 'Out of Stock').length;

  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Dashboard Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-4 rounded-lg border-2">
            <h3 className="text-lg font-semibold text-gray-900">Total Parts</h3>
            <p className="text-3xl font-bold text-gray-700">{totalParts}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border-2">
            <h3 className="text-lg font-semibold text-gray-900">Low Stock</h3>
            <p className="text-3xl font-bold text-yellow-600">{lowStockCount}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border-2">
            <h3 className="text-lg font-semibold text-gray-900">Out of Stock</h3>
            <p className="text-3xl font-bold text-red-600">{outOfStockCount}</p>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Search Input */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search Part Number or Description"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                />
              </div>
              {/* Actions Menu */}
              <div className="md:self-end">
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150">
                      Actions
                      <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setCreatePartModalOpen(true)}
                              className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block w-full text-left px-4 py-2 text-sm transition duration-150`}
                            >
                              Create Part
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="bg-white rounded-lg border-2 overflow-x-auto">
  <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-gray-50">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('part_number')}>
          Part Number {sortField === 'part_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('shop')}>
          Primary Shop {sortField === 'shop' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('inventory_quantity')}>
          Current Stock {sortField === 'inventory_quantity' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('drawdown')}>
          Job Drawdown {sortField === 'drawdown' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('available_stock')}>
          Available Stock {sortField === 'available_stock' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {filteredInventory.map((item) => (
        <tr key={item.part_number} className="hover:bg-gray-50 transition duration-150">
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            <button
              onClick={() => navigate(`/part/${item.part_number}`)}
              className="text-indigo-600 hover:text-indigo-800 transition duration-150"
            >
              {item.part_number}
            </button>
          </td>
          <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={item.description}>{item.description}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.shop}</td>
          
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.inventory_quantity}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.drawdown}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStockStatusClass(item.available_stock, item.minimum_stock_level)}`}>
              {item.available_stock}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex space-x-2">
            <button
              onClick={() => viewPartHistory(item.part_number)}
              className="text-blue-600 hover:text-blue-800 transition duration-150"
              title="View History"
            >
              <EyeIcon className="w-5 h-5" />
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

        {/* Part History Modal */}
        <Transition appear show={historyModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={() => setHistoryModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle border-2 transition-all">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Part History: {selectedPart}
                    </Dialog.Title>
                    <div className="mt-4">
                      <h4 className="text-md font-medium text-gray-900 mb-2">Recent Production Logs</h4>
                      {partHistory.productionLogs.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity Produced</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Production Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {partHistory.productionLogs.map((log, index) => (
                                <tr key={index}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.quantity_produced}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(log.production_date).toLocaleDateString()}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.shops?.name || 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-gray-500">No recent production logs.</p>
                      )}

                      <h4 className="text-md font-medium text-gray-900 mt-6 mb-2">Recent Deliveries</h4>
                      {partHistory.deliveries.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity Shipped</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ship Date</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {partHistory.deliveries.map((delivery, index) => (
                                <tr key={index}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.quantity}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(delivery.ship_date).toLocaleDateString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-gray-500">No recent deliveries.</p>
                      )}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200"
                        onClick={() => setHistoryModalOpen(false)}
                      >
                        Close
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Create Part Modal */}
        <Transition appear show={createPartModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={() => setCreatePartModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle border-2 transition-all">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Create New Part
                    </Dialog.Title>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Part Number Master</label>
                        <input
                          type="text"
                          value={newPart.part_number_master}
                          onChange={(e) => setNewPart({ ...newPart, part_number_master: e.target.value })}
                          className={`mt-1 block w-full p-2 border ${formErrors.part_number_master ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150`}
                          placeholder="Enter part number master (e.g., 25-1234)"
                        />
                        {formErrors.part_number_master && (
                          <p className="mt-1 text-sm text-red-500">{formErrors.part_number_master}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Revision</label>
                        <input
                          type="text"
                          value={newPart.revision}
                          onChange={(e) => setNewPart({ ...newPart, revision: e.target.value })}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                          placeholder="Enter revision (e.g., 1, A, or leave blank for 0)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          value={newPart.description}
                          onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                          className={`mt-1 block w-full p-2 border ${formErrors.description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150`}
                          placeholder="Enter description"
                          rows="3"
                        />
                        {formErrors.description && (
                          <p className="mt-1 text-sm text-red-500">{formErrors.description}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Initial Inventory Quantity</label>
                        <input
                          type="number"
                          value={newPart.inventory_quantity}
                          onChange={(e) => setNewPart({ ...newPart, inventory_quantity: e.target.value })}
                          className={`mt-1 block w-full p-2 border ${formErrors.inventory_quantity ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150`}
                          placeholder="Enter initial quantity"
                          min="0"
                        />
                        {formErrors.inventory_quantity && (
                          <p className="mt-1 text-sm text-red-500">{formErrors.inventory_quantity}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Minimum Stock Level</label>
                        <input
                          type="number"
                          value={newPart.minimum_stock_level}
                          onChange={(e) => setNewPart({ ...newPart, minimum_stock_level: e.target.value })}
                          className={`mt-1 block w-full p-2 border ${formErrors.minimum_stock_level ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150`}
                          placeholder="Enter minimum stock level"
                          min="0"
                        />
                        {formErrors.minimum_stock_level && (
                          <p className="mt-1 text-sm text-red-500">{formErrors.minimum_stock_level}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end space-x-4">
                      <button
                        type="button"
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200"
                        onClick={() => {
                          setNewPart({
                            part_number_master: '',
                            revision: '',
                            description: '',
                            inventory_quantity: '',
                            minimum_stock_level: '',
                          });
                          setFormErrors({});
                          setCreatePartModalOpen(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
                        onClick={handleCreatePart}
                      >
                        Create Part
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
}

export default Parts;